import axios from 'axios';
import { config } from '../config';
import SessionService from './SessionService';

const API_URL = config.API_BASE_URL;

export type SparkConnectionSessionStatus =
  | 'pending'
  | 'linking'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'superseded';

export interface SparkConnectionSession {
  sessionId: string | null;
  continuationId: string;
  status: SparkConnectionSessionStatus;
  expiresAt: string;
  connectionName: string;
  sectorId: number | null;
  displayPhone: string | null;
  reason: string | null;
  userMessage: string | null;
  url: string | null;
}

function authHeaders() {
  return { Authorization: `Bearer ${SessionService.getToken()}` };
}

export function sparkConnectionErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export async function getSparkConnectionAvailability(): Promise<boolean> {
  const response = await axios.get<{ available?: boolean }>(
    `${API_URL}/spark/connection-sessions/availability`,
    { headers: authHeaders() }
  );
  return response.data?.available === true;
}

export async function startSparkConnectionSession(input: {
  connectionName: string;
  sectorId?: number;
}): Promise<SparkConnectionSession> {
  const response = await axios.post<SparkConnectionSession>(
    `${API_URL}/spark/connection-sessions`,
    {
      connectionName: input.connectionName,
      sectorId: input.sectorId,
    },
    { headers: authHeaders() }
  );
  return response.data;
}

export async function getSparkConnectionSession(id: string): Promise<SparkConnectionSession> {
  const response = await axios.get<SparkConnectionSession>(
    `${API_URL}/spark/connection-sessions/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  return response.data;
}

export async function getCurrentSparkConnectionSession(): Promise<SparkConnectionSession | null> {
  const response = await axios.get<SparkConnectionSession | ''>(
    `${API_URL}/spark/connection-sessions/current`,
    {
      headers: authHeaders(),
      validateStatus: (status) => status === 200 || status === 204,
    }
  );
  if (response.status === 204 || !response.data) return null;
  return response.data;
}
